<script lang="ts">
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Check } from 'lucide-svelte';
  import { adminProjectsApi, adminUsersApi } from '$lib/api/admin';
  import { projectsApi } from '$lib/api/projects';
  import { fetchMe } from '$lib/api/me';
  import { getErrorMessage } from '$lib/api/client';
  import { teamLabel } from '$lib/utils/format';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import Avatar from '$lib/components/ui/Avatar.svelte';
  import type { AdminUser, Project } from '$lib/types';
  import { TEAMS } from '$lib/types';

  // memberMode is the reduced, member-facing variant (sidebar "New
  // project"): no team select (forced server-side to the caller's own
  // team), no grants section, no status field — and it adds the prefix
  // field admins don't need in this modal (admin create leaves prefix
  // empty and sets it later; a member-created project needs one
  // immediately so its board can mint task keys). Always a create, never
  // editing.
  const {
    project,
    members,
    memberMode = false,
    onClose
  }: { project: Project | null; members: AdminUser[]; memberMode?: boolean; onClose: () => void } = $props();

  const queryClient = useQueryClient();
  const editing = !memberMode && !!project;

  let name = $state(project?.name ?? '');
  let prefix = $state(project?.prefix ?? '');
  let status = $state(project?.status ?? 'active');
  let team = $state(project?.team ?? 'fullstack');
  let prodUrl = $state(project?.prod_url ?? '');
  let repo = $state(project?.repo ?? '');
  let erp = $state(project?.ref_erp ?? '');
  let hwRev = $state(project?.ref_hw_rev ?? '');
  let artifact = $state(project?.ref_artifact ?? '');
  let wipInProgress = $state(project?.wip?.['in-progress']?.toString() ?? '');
  let wipReview = $state(project?.wip?.review?.toString() ?? '');

  // This list only ever reads/writes a member's *extra* grants (projects
  // array) — a member whose PRIMARY project is this one already has access
  // and isn't affected by (or reflected in) that array; see setUserProjects
  // in internal/api/handlers/admin_users.go, which never touches
  // project_id. Those rows render checked-and-locked below instead of
  // joining `grants`, so toggling never produces a no-op diff that silently
  // fails to change anything.
  const primaryIds = new Set(members.filter((m) => project && m.project_id === project.id).map((m) => m.id));
  const initialGrantIds = new Set(members.filter((m) => project && m.projects.includes(project.id)).map((m) => m.id));
  let grants = $state<number[]>([...initialGrantIds]);
  let formError = $state('');

  // Teams are a hard boundary (internal/models/team.go): a grant can never
  // cross one, so the picker only ever offers members already in the
  // project's (selected) team — anyone else is hidden with a count, not
  // silently offered and then rejected by the backend on save.
  const teamMembers = $derived(members.filter((m) => m.team === team));
  const hiddenByTeam = $derived(members.length - teamMembers.length);

  function toggleGrant(id: number) {
    if (primaryIds.has(id)) return; // locked: change their "Primary project" in Users & access instead
    grants = grants.includes(id) ? grants.filter((x) => x !== id) : [...grants, id];
  }

  const saveMutation = createMutation(() => ({
    mutationFn: async () => {
      const wip = {
        ...(Number(wipInProgress) > 0 ? { 'in-progress': Number(wipInProgress) } : {}),
        ...(Number(wipReview) > 0 ? { review: Number(wipReview) } : {})
      };

      if (memberMode) {
        return projectsApi.create({
          name: name.trim(),
          prefix: prefix.trim(),
          prod_url: prodUrl.trim(),
          repo: repo.trim(),
          ref_erp: erp.trim(),
          ref_artifact: artifact.trim(),
          ref_hw_rev: hwRev.trim(),
          wip
        });
      }

      const body = {
        name: name.trim(),
        status,
        team,
        prod_url: prodUrl.trim(),
        repo: repo.trim(),
        ref_erp: erp.trim(),
        ref_artifact: artifact.trim(),
        ref_hw_rev: hwRev.trim(),
        wip
      };
      const saved = editing ? await adminProjectsApi.update(project!.id, body) : await adminProjectsApi.create(body);

      // Grant management stays on the existing admin/users endpoint — diff
      // each member's `projects` array against the checkbox state.
      const changed = members.filter((m) => grants.includes(m.id) !== initialGrantIds.has(m.id));
      await Promise.all(
        changed.map((m) => {
          const has = grants.includes(m.id);
          const projects = has ? [...m.projects, saved.id] : m.projects.filter((id) => id !== saved.id);
          return adminUsersApi.update(m.id, { projects });
        })
      );
      return saved;
    },
    onSuccess: async (saved: Project) => {
      if (memberMode) {
        // session.me (the sidebar's project list) isn't a TanStack query —
        // it's bootstrapped once at login — so it needs an explicit refetch
        // alongside the ['projects'] cache the workspace page reads from.
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        session.setMe(await fetchMe());
        onClose();
        toast.success('Project created');
        route.push({ tab: 'project', projectId: saved.id });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['admin-projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!name.trim()) {
      formError = 'Project name is required';
      return;
    }
    if (memberMode && !prefix.trim()) {
      formError = 'Task key prefix is required';
      return;
    }
    formError = '';
    saveMutation.mutate();
  }
</script>

<Modal title={editing ? 'Edit project' : 'New project'} {onClose} wide>
  {#snippet footer()}
    <span class="spacer"></span>
    <button type="button" class="btn btn-secondary" onclick={onClose}>Cancel</button>
    <button
      type="submit"
      form="project-form"
      class="btn btn-primary"
      disabled={!name.trim() || (memberMode && !prefix.trim()) || saveMutation.isPending}
    >
      {editing ? 'Save changes' : 'Create project'}
    </button>
  {/snippet}

  <form id="project-form" onsubmit={submit}>
    <div class="field">
      <label class="label" for="pm-name">Project name</label>
      <input id="pm-name" class="input" placeholder="e.g. WizHom Pro Backend" bind:value={name} />
    </div>
    {#if memberMode}
      <div class="field">
        <label class="label" for="pm-prefix">Task key prefix</label>
        <input id="pm-prefix" class="input" placeholder="e.g. WHP" maxlength="8" bind:value={prefix} />
        <div style="font-size:11.5px;color:var(--t3);margin-top:4px">Used to generate task keys, e.g. {(prefix.trim() || 'WHP').toUpperCase()}-101.</div>
      </div>
    {:else}
      <div class="field">
        <label class="label" for="pm-status">Status</label>
        <select id="pm-status" class="select" bind:value={status}>
          {#each ['active', 'paused', 'archived'] as s (s)}
            <option value={s}>{s[0].toUpperCase() + s.slice(1)}</option>
          {/each}
        </select>
      </div>
    {/if}
    {#if !memberMode}
      <div class="field">
        <label class="label" for="pm-team">Team</label>
        <select id="pm-team" class="select" bind:value={team}>
          {#each TEAMS as t (t)}
            <option value={t}>{teamLabel(t)}</option>
          {/each}
        </select>
        <div style="font-size:11.5px;color:var(--t3);margin-top:4px">
          Hard boundary — only same-team members can be granted access below. Changing team requires this project to have
          no members with access yet.
        </div>
      </div>
    {/if}
    <div class="field">
      <label class="label" for="pm-prod">Production URL</label>
      <input id="pm-prod" class="input" placeholder="https://…  (optional for firmware)" bind:value={prodUrl} />
    </div>
    <div class="field">
      <label class="label" for="pm-repo">GitHub repo</label>
      <input id="pm-repo" class="input" placeholder="toyama-controls/repo-name" bind:value={repo} />
    </div>
    <div style="display:flex;gap:12px">
      <div class="field" style="flex:1">
        <label class="label" for="pm-erp">ERP part #</label>
        <input id="pm-erp" class="input" placeholder="TYM-PCB-000" bind:value={erp} />
      </div>
      <div class="field" style="flex:1">
        <label class="label" for="pm-hw">Hardware rev</label>
        <input id="pm-hw" class="input" placeholder="Rev A" bind:value={hwRev} />
      </div>
    </div>
    <div class="field">
      <label class="label" for="pm-artifact">Artifact store</label>
      <input id="pm-artifact" class="input" placeholder="artifacts.toyama.io/repo" bind:value={artifact} />
    </div>
    <div style="display:flex;gap:12px">
      <div class="field" style="flex:1">
        <label class="label" for="pm-wip1">WIP limit — In progress</label>
        <input id="pm-wip1" class="input" type="number" min="0" placeholder="none" bind:value={wipInProgress} />
      </div>
      <div class="field" style="flex:1">
        <label class="label" for="pm-wip2">WIP limit — Review</label>
        <input id="pm-wip2" class="input" type="number" min="0" placeholder="none" bind:value={wipReview} />
      </div>
    </div>
    {#if !memberMode}
      <div class="field">
        <span class="label">Developer access</span>
        <div>
          {#each teamMembers as m (m.id)}
            {@const locked = primaryIds.has(m.id)}
            {@const on = locked || grants.includes(m.id)}
            <div
              class="grant-row {on ? 'on' : ''}"
              style={locked ? 'cursor:default' : ''}
              role="button"
              tabindex="0"
              onclick={() => toggleGrant(m.id)}
              onkeydown={(e) => e.key === 'Enter' && toggleGrant(m.id)}
            >
              <span class="checkbox">{#if on}<Check size={13} strokeWidth={3} />{/if}</span>
              <Avatar id={m.id} name={m.name || m.email} size={26} />
              <span style="font-size:13.5px;color:var(--t1);font-weight:500">{m.name || m.email}</span>
              {#if locked}<span style="font-size:11px;color:var(--t3);margin-left:auto">Primary project</span>{/if}
            </div>
          {:else}
            <div style="font-size:12.5px;color:var(--t3)">No {teamLabel(team).toLowerCase()} members yet.</div>
          {/each}
          {#if hiddenByTeam > 0}
            <div style="font-size:11.5px;color:var(--t3);margin-top:4px">
              {hiddenByTeam} member(s) hidden — different team.
            </div>
          {/if}
          <div style="font-size:11.5px;color:var(--t3);margin-top:4px">Access changes apply immediately once saved.</div>
        </div>
      </div>
    {/if}
    {#if formError}<p style="color:var(--danger);font-size:13px">{formError}</p>{/if}
  </form>
</Modal>
